import React from 'react';
import {Box, MenuItem, Select, Typography} from '@mui/material';
import {ExpandMore} from '@mui/icons-material';
import {useInput, useTranslate, required as raRequired} from 'react-admin';
import {useFormContext} from 'react-hook-form';
import {t} from '../../themes/statamicTokens';

const composeValidate = (required, validate) => {
    const list = [];
    if (required === true) list.push(raRequired());
    else if (typeof required === 'function') list.push(required);
    else if (Array.isArray(required)) list.push(...required);
    if (Array.isArray(validate)) list.push(...validate);
    else if (typeof validate === 'function') list.push(validate);
    return list.length === 0 ? undefined : list;
};

const StatamicSelectInput = ({
    source,
    label,
    helperText,
    required,
    validate,
    choices = [],
    defaultValue,
    placeholder = 'Select…',
    disabled = false,
}) => {
    const translate = useTranslate();
    const {field, fieldState} = useInput({
        source,
        defaultValue,
        validate: composeValidate(required, validate),
    });

    const fc = useFormContext();
    const submitAttempted = Boolean(fc?.formState?.isSubmitted);

    const rawError = fieldState.error;
    const showError = (fieldState.isTouched || submitAttempted) && rawError;
    const errorMessage = showError
        ? (typeof rawError === 'string'
            ? translate(rawError, {_: rawError})
            : translate(rawError.message ?? 'ra.validation.required', {_: rawError.message ?? 'Required'}))
        : null;

    const isRequired = required === true || typeof required === 'function' || Array.isArray(required);

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5}}>
            {label && (
                <Box sx={{display: 'flex', alignItems: 'baseline', gap: 0.5}}>
                    <Typography sx={{fontSize: `${t.type.caption.size}px`, fontWeight: 500, color: t.color.text.secondary}}>
                        {label}
                    </Typography>
                    {isRequired && (
                        <Typography component="span" sx={{fontSize: `${t.type.caption.size}px`, color: t.color.status.dangerSolid}}>
                            *
                        </Typography>
                    )}
                </Box>
            )}
            {helperText && (
                <Typography sx={{fontSize: 11, color: t.color.text.muted, mt: -0.25, mb: 0.25}}>
                    {helperText}
                </Typography>
            )}
            <Select
                {...field}
                value={field.value ?? ''}
                disabled={disabled}
                displayEmpty
                IconComponent={ExpandMore}
                renderValue={(val) => {
                    if (!val && val !== 0) return <span style={{color: t.color.text.muted}}>{placeholder}</span>;
                    const match = choices.find(c => c.id === val);
                    return match ? match.name : val;
                }}
                sx={{
                    fontSize: `${t.type.body.size}px`,
                    backgroundColor: disabled ? t.color.sidebarHover : t.color.surface,
                    opacity: disabled ? 0.7 : 1,
                    minHeight: 36,
                    '& .MuiSelect-select': {
                        py: 0.75,
                        px: 1.25,
                        minHeight: 'unset !important',
                    },
                    '& fieldset': {
                        borderColor: `${showError ? t.color.status.dangerSolid : t.color.border.default} !important`,
                        borderRadius: `${t.radius.sm}px`,
                    },
                    '&:hover fieldset': { borderColor: `${t.color.border.strong} !important` },
                    '&.Mui-focused fieldset': {
                        borderColor: `${showError ? t.color.status.dangerSolid : t.color.accent.primary} !important`,
                        boxShadow: `0 0 0 3px ${showError ? t.color.status.dangerTint : t.color.accent.primaryTint}`,
                    },
                    '& .MuiSelect-icon': { color: t.color.text.muted },
                }}
            >
                {choices.map(c => (
                    <MenuItem key={c.id} value={c.id} sx={{fontSize: `${t.type.body.size}px`}}>
                        {c.name}
                    </MenuItem>
                ))}
            </Select>
            {errorMessage && (
                <Typography sx={{fontSize: 11, color: t.color.status.dangerSolid}}>
                    {errorMessage}
                </Typography>
            )}
        </Box>
    );
};

export default StatamicSelectInput;
